import { useState, useMemo, useCallback } from 'react';
import { useCrmData } from '../../../contexts/CrmProviders';
import { useChatContext } from '../../../contexts/useChatContext';
import { ChatConversation } from './useChat';
import { Contact } from '../../../types';
import { cleanPhone, generateJidVariants } from '../../../utils/phoneUtils';
import { supabase } from '../../../lib/supabase';
import { notify } from '../../../utils/toast';

// Hooks extraídos
import { useContactPanelDeals } from './useContactPanelDeals';
import { useContactPanelTasks } from './useContactPanelTasks';
import { useContactPanelConversation } from './useContactPanelConversation';
import { contactMatchesPhone } from './contactPanelDeals.helpers';

export const useContactPanel = (conversation: ChatConversation | undefined, connectionId?: string) => {
    const { contacts, saveLead, crmUsers, updateContact, createContact } = useCrmData();
    const { updateConversation } = useChatContext();

    const [openSections, setOpenSections] = useState({
        deals: true,
        tasks: true,
        contactInfo: true,
        origin: true,
        conversationInfo: true,
        observations: false
    });

    // Hook de Conversa (Tags e Responsável)
    const {
        conversationResponsible,
        conversationTags: baseConversationTags,
        handleConversationResponsibleChange,
        handleConversationTagsChange: baseHandleConversationTagsChange
    } = useContactPanelConversation(conversation, connectionId);

    // Busca o contato correspondente pelo telefone (Movido para cima para ser passado ao useContactPanelDeals)
    const currentContact = useMemo(() => {
        if (!conversation) return null;
        const phone = cleanPhone(conversation.id);
        return contacts.find((contact) => contactMatchesPhone(contact, phone)) || null;
    }, [conversation, contacts]);

    // Hook de Deals
    const {
        currentLeads,
        funnelStages,
        isCreatingDeal,
        handleCreateDeal,
        handleStageChange,
        handleLeadUpdate
    } = useContactPanelDeals(conversation, baseConversationTags, conversationResponsible, currentContact);

    // Sincronização de Tags: Prioriza o Lead atual se existir, senão usa a Conversa
    // Isso garante que se editarmos no Kanban, aparece aqui.
    const displayTags = useMemo(() => {
        const leadTags = currentLeads[0]?.tags;
        if (leadTags?.length) return leadTags;
        return baseConversationTags;
    }, [currentLeads, baseConversationTags]);

    // Handler unificado para atualizar tags em ambos (Lead e Conversa)
    const handleConversationTagsChange = async (tagIds: string[]) => {
        // 1. Atualiza na conversa (visual imediato no chat e banco chat_conversations)
        await baseHandleConversationTagsChange(tagIds);

        // 2. Se houver lead ativo, atualiza nele também (sincroniza com Kanban)
        if (currentLeads.length > 0) {
            const activeLead = currentLeads[0];
            const updatedLead = { ...activeLead, tags: tagIds };
            await saveLead(updatedLead);
        }
    };

    // Hook de Tasks - depende de currentLeads
    const {
        currentTasks,
        isCreatingTask,
        setIsCreatingTask,
        handleCreateTask,
        handleToggleTaskCompletion,
        handleUpdateTask,
        handleDeleteTask,
        handleDuplicateTask
    } = useContactPanelTasks(conversation, currentLeads);

    const toggleSection = (section: keyof typeof openSections) => {
        setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));
    };

    // Atualizar campo específico do contato
    const handleUpdateContactField = useCallback(async (field: string, value: any) => {
        // Cenário 1: Contato já existe -> Atualiza
        if (currentContact?.id) {
            try {
                await updateContact(currentContact.id, { [field]: value });
            } catch (error) {
                console.error('Error updating contact field:', error);
            }
            return;
        }

        // Cenário 2: Contato não existe -> Cria novo (Upsert Logic)
        if (conversation?.id) {
            try {
                const phone = cleanPhone(conversation.id);
                const newContactData: Omit<Contact, 'id'> = {
                    name: conversation.name || phone,
                    whatsapp: phone,
                    email: field === 'email' ? value : '',
                    company: field === 'company' ? value : '',
                    phone,
                    tags: [],
                    customFields: {},
                    ...(field === 'origin_id' ? { origin_id: value || null } : {}),
                };

                await createContact(newContactData);
            } catch (error) {
                console.error('Error creating contact from panel:', error);
            }
        }
    }, [currentContact, updateContact, conversation, createContact]);

    const conversationPhone = conversation ? cleanPhone(conversation.id) : '';

    const handleSaveContact = useCallback(async (data: Omit<Contact, 'id'>) => {
        const name = data.name?.trim();
        const fields: Partial<Contact> = {
            name,
            email: data.email,
            company: data.company,
            origin_id: data.origin_id,
        };

        try {
            if (currentContact?.id) {
                await updateContact(currentContact.id, fields);
                notify.success('Contato atualizado com sucesso!');
            } else if (conversation) {
                await createContact({
                    ...fields,
                    name: name || conversation.name,
                    whatsapp: conversationPhone,
                    phone: conversationPhone,
                    tags: [],
                    customFields: {},
                });
                notify.success('Contato criado com sucesso!');
            } else {
                return;
            }

            if (conversation && name) {
                updateConversation(conversation.id, { name });
                if (connectionId) {
                    await supabase
                        .from('chat_conversations')
                        .update({ contact_name: name })
                        .in('remote_jid', generateJidVariants(conversation.id))
                        .eq('connection_id', connectionId);
                }
            }
        } catch (error) {
            notify.error(error instanceof Error ? error.message : 'Erro ao salvar contato.');
            throw error;
        }
    }, [currentContact, conversation, conversationPhone, connectionId, updateContact, createContact, updateConversation]);

    return {
        // State
        openSections,
        toggleSection,

        // Task State
        isCreatingTask,
        setIsCreatingTask,

        // Data
        currentLeads,
        currentTasks,
        currentContact,
        funnelStages,
        crmUsers,
        isCreatingDeal,

        // Actions
        handleStageChange,
        handleLeadUpdate,
        handleCreateDeal,
        handleCreateTask,
        handleToggleTaskCompletion,
        handleUpdateTask,
        handleDeleteTask,
        handleDuplicateTask,
        handleUpdateContactField,
        handleSaveContact,

        // Conversation Info
        conversationResponsible,
        conversationTags: displayTags,
        handleConversationResponsibleChange,
        handleConversationTagsChange
    };
};

